import { ColumnDef } from "@tanstack/react-table";
import { EditIcon, PlusInSquareIcon, TrashAltIcon } from "assets/icons";
import { useDebouncedValue } from "hooks";
import {
  CompanyManager,
  useDeleteManager,
  useGetManagers
} from "modules/companyAdminAuth";
import { useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import {
  Avatar,
  Button,
  ItemsTable,
  SearchInput,
  TableItemActionsControl
} from "ui";

import { NewManagerModal } from "../../components/NewManagerModal";
import { useCurrentPortalUser } from "../../hooks";

import styles from "./CompanyStaff.module.scss";

const CompanyStaff = () => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue<string>(search, 400);

  const { manager: currentManager } = useCurrentPortalUser();
  const selfId = currentManager?.id;

  const { managers, isLoading } = useGetManagers(debouncedSearch || undefined);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editable, setEditable] = useState<CompanyManager | null>(null);
  const [openedActionsIndex, setOpenedActionsIndex] = useState<number | null>(
    null
  );

  const { mutate: deleteManager } = useDeleteManager({
    onSuccess: () => toast.success(t("Manager deleted")),
    onError: (e) => toast.error(e?.message || t("Failed to delete"))
  });

  const openCreate = () => {
    setEditable(null);
    setIsModalOpen(true);
  };

  const openEdit = (m: CompanyManager) => {
    setEditable(m);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditable(null);
  };

  const onDelete = (m: CompanyManager) => {
    if (window.confirm(t("Delete this manager?"))) {
      deleteManager(m.id);
    }
  };

  const columns: ColumnDef<CompanyManager>[] = useMemo(
    () => [
      {
        id: "user",
        header: t("User"),
        size: 32,
        minSize: 32,
        cell: ({ row }) => {
          const m = row.original;
          const fullName = `${m.firstname} ${m.lastname}`.trim();
          return (
            <div className={styles.staff__nameCell}>
              <Avatar
                image={
                  m.avatar
                    ? ({ url: m.avatar.url, id: m.avatar.id } as never)
                    : undefined
                }
                dimensions={{ width: 36, height: 36, borderRadius: "50%" }}
              />
              <span className={styles.staff__name}>{fullName}</span>
            </div>
          );
        }
      },
      {
        id: "email",
        header: t("Email"),
        size: 32,
        minSize: 32,
        cell: ({ row }) => row.original.email
      },
      {
        id: "role",
        header: t("Role"),
        size: 32,
        minSize: 32,
        cell: () => "Moderator"
      },
      {
        id: "actions",
        header: () => null,
        size: 4,
        minSize: 4,
        cell: ({ row }) => {
          if (selfId && row.original.id === selfId) return null;
          return (
            <TableItemActionsControl
              rowIndex={row.index}
              openedItemOptionsIndex={openedActionsIndex}
              setOpenedItemOptionsIndex={setOpenedActionsIndex}
              actionsList={[
                {
                  label: t("Edit"),
                  Icon: <EditIcon fill="#090914" width={18} height={18} />,
                  onClick: () => openEdit(row.original)
                },
                {
                  label: t("Delete"),
                  Icon: <TrashAltIcon fill="#ff0033" width={18} height={18} />,
                  labelColor: "#ff0033",
                  onClick: () => onDelete(row.original)
                }
              ]}
            />
          );
        }
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [openedActionsIndex, selfId]
  );

  return (
    <div className={styles.staff}>
      <div className={styles.staff__header}>
        <h2 className={styles.staff__title}>{t("Staff Management")}</h2>
        <Button
          icon={<PlusInSquareIcon width={20} height={20} />}
          onClick={openCreate}
        >
          {t("New Manager")}
        </Button>
      </div>

      <div className={styles.staff__card}>
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder={t("Search...")}
        />

        <ItemsTable
          className={styles.staff__table}
          columns={columns}
          data={managers}
          isLoading={isLoading}
          skeletonHeight={56}
        />
      </div>

      <NewManagerModal
        isOpen={isModalOpen}
        editableManager={editable}
        onClose={closeModal}
      />
    </div>
  );
};

export default CompanyStaff;
